// src/pages/BillingScreen.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import { createCreditBillFromInvoice, COLLECTION_BILLS } from "../services/creditDueService";
import { useAuth } from "../context/AuthContext";
import { BILLING_CREDIT_OUTLET_STORAGE_KEY, getVisibleAppBranches } from "../config/appBranches";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import PartyAutoComplete from "../components/PartyAutoComplete";
import VoucherHeader from "../components/VoucherHeader";
import computeCommissionsForSave from "../helpers/commissions";
import toast, { Toaster } from "react-hot-toast";
import {
  collection, getDocs, updateDoc, deleteDoc,
  doc as firestoreDoc, query, where, limit, addDoc, serverTimestamp, orderBy
} from "firebase/firestore";
import { getDoc } from "firebase/firestore";
import { startProcessing, stopProcessing, getAllQueueItems } from "../services/offlineQueue";
import QueuedInvoicesModal from "../components/QueuedInvoicesModal";
import { Settings2 } from "lucide-react";
import {
  createExactVoucherTypeMatcher,
  forEachVoucherTypeRowFromSalesImport,
} from "../services/billingImportFindService";
import { DEFAULT_OUTWARD_PAGE } from "../config/voucherTransactionPages";
import {
  saveBillingFindVoucher,
  deleteBillingFindVoucher,
  generateBillingVoucherNo,
  productIdFromItem,
} from "../services/billingImportSaveService";
import BillingFindVoucherModal from "../components/BillingFindVoucherModal";
import {
  normalizeProductMasterDoc,
  PRODUCT_MASTER_COLLECTION,
} from "../config/productMasterFirestore";
import {
  dedupeProductList,
  filterAndSortCachedProducts,
  findProductMasterByPartNo,
  queryProductMasterByPartPrefix,
  warmProductMasterCache,
} from "../services/productMasterSearch";

/* Constants */
const DEFAULT_GST = 18;
const DEFAULT_UNIT = "Nos";

function resolveUnit(unit) {
  const u = String(unit ?? "").trim();
  return u || DEFAULT_UNIT;
}

/** Line grid column widths — per voucher slug (order matches `cols` / table `<colgroup>`). */
function columnWidthsStorageKey(slug) {
  return `voucher_col_widths_${slug || "outward-supply"}_v1`;
}
const COL_WIDTH_MIN = 32;
const COL_WIDTH_MAX = 520;
const DEFAULT_BILLING_COL_WIDTHS = [
  40, 120, 180, 70, 50, 80, 82, 70, 70, 65, 70, 85, 65, 85, 70, 70, 50,
];

function loadBillingColumnWidths(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [...DEFAULT_BILLING_COL_WIDTHS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== DEFAULT_BILLING_COL_WIDTHS.length) {
      return [...DEFAULT_BILLING_COL_WIDTHS];
    }
    return parsed.map((n, i) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return DEFAULT_BILLING_COL_WIDTHS[i];
      return Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, Math.round(x)));
    });
  } catch {
    return [...DEFAULT_BILLING_COL_WIDTHS];
  }
}

function saveBillingColumnWidths(widths, storageKey) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch (e) {
    console.warn("saveBillingColumnWidths", e);
  }
}
const MRP_TO_RATE_MULTIPLIER = 0.78125;
/** Max rows in code dropdown (large catalogs: stop scanning after this many matches). */
const ITEM_DROPDOWN_MAX = 80;
/** Rows shown when search box is empty (cache only — no full-catalog load). */
const ITEM_EMPTY_PREVIEW = 25;
/** Max items kept in memory from searches / selections. */
const ITEM_CACHE_MAX = 400;
/** Debounce ms before merging Firestore results (local matches show immediately). */
const ITEM_SEARCH_DEBOUNCE_MS = 200;
const COMMISSION_RECOMPUTE_DEBOUNCE_MS = 800;
const FIND_CACHE_TTL_MS = 60 * 1000;

function normalizeLookupKey(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const compact = s.replace(/,/g, "");
  if (/^-?\d+(\.\d+)?$/.test(compact)) {
    const n = Number(compact);
    if (Number.isFinite(n)) return String(Math.trunc(n));
  }
  return compact;
}

function parseMoneyLike(v, fallback = 0) {
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : fallback;
}

/** @deprecated Use normalizeProductMasterDoc */
const normalizeFirestoreItemDoc = normalizeProductMasterDoc;

function registerItemInLookupMap(map, it) {
  const add = (key) => {
    const k = String(key ?? "").trim().toLowerCase();
    if (k) map.set(k, it);
  };
  add(it.partno);
  add(it.code);
  add(it.name);
  add(it.barcode);
  if (it.productid != null && it.productid !== "") {
    add(String(it.productid));
    const norm = normalizeLookupKey(it.productid);
    if (norm) add(norm);
  }
}

function cacheItemInRef(cacheRef, it) {
  if (!it) return;
  registerItemInLookupMap(cacheRef.current, it);
}

function lookupFromItemCache(cacheRef, code) {
  const key = String(code ?? "").trim().toLowerCase();
  if (!key) return null;
  return cacheRef.current.get(key) || null;
}

/* stable empty row template at module level so identity doesn't change each render */
const EMPTY_ROW_TEMPLATE = {
  code: "",
  desc: "",
  qty: "",
  unit: DEFAULT_UNIT,
  rate: "",
  discount: "",
  gst: DEFAULT_GST,
  mrp: "",
  productId: "",
  productid: "",
  commissionAmount: 0,
};

/* small UI helper */
const SideBtn = ({ children, className = "", onClick, disabled }) => (
  <button
    className={`w-full text-left border border-gray-400 bg-white hover:bg-gray-100 px-2 py-2 ${className}`}
    onClick={onClick}
    type="button"
    disabled={disabled}
  >
    {children}
  </button>
);

/* Quick Add Party Modal */
function QuickAddPartyModal({ open, onClose, onCreated }) {
  const nameRef = useRef(null);
  const [name, setName] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTimeout(() => nameRef.current?.focus?.(), 60);
    else { setName(""); setGstin(""); setAddress(""); setPhone(""); setSaving(false); }
  }, [open]);

  const handleCreate = async () => {
    if (!name || !name.trim()) return toast.error("Enter party name");
    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, "customers"), {
        partyName: name.trim(),
        gstin: gstin.trim(),
        address: address.trim(),
        phone: phone.trim(),
        group: "Sundry Debtors",
        createdAt: serverTimestamp(),
      });
      const newParty = {
        id: docRef.id,
        partyName: name.trim(),
        gstin: gstin.trim(),
        address: address.trim(),
        phone: phone.trim(),
        group: "Sundry Debtors",
      };
      onCreated?.(newParty);
      onClose();
      toast.success("Party added successfully");
    } catch (err) {
      console.error("Error creating party:", err);
      toast.error("Failed to create party");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded shadow-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold">Quick Add Party</div>
          <button className="text-red-500" onClick={onClose} type="button">Close</button>
        </div>

        <div className="space-y-2 text-sm">
          <label className="block">
            <div className="text-xs">Name</div>
            <input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} className="w-full border px-2 py-1" />
          </label>
          <label className="block">
            <div className="text-xs">GSTIN</div>
            <input value={gstin} onChange={(e) => setGstin(e.target.value)} className="w-full border px-2 py-1" />
          </label>
          <label className="block">
            <div className="text-xs">Address</div>
            <textarea value={address} onChange={(e) => setAddress(e.target.value)} className="w-full border px-2 py-1" />
          </label>
          <label className="block">
            <div className="text-xs">Phone</div>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full border px-2 py-1" />
          </label>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 border rounded" type="button">Cancel</button>
          <button onClick={handleCreate} className="px-3 py-1 bg-blue-600 text-white rounded" disabled={saving} type="button">
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Item Create Modal */
function ItemCreateModal({ open, onClose, onCreated }) {
  const codeRef = useRef(null);
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [unit, setUnit] = useState(DEFAULT_UNIT);
  const [rate, setRate] = useState("");
  const [mrp, setMrp] = useState("");
  const [barcode, setBarcode] = useState("");
  const [gst, setGST] = useState(DEFAULT_GST);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTimeout(() => codeRef.current?.focus?.(), 60);
    else {
      setCode("");
      setDesc("");
      setUnit(DEFAULT_UNIT);
      setRate("");
      setMrp("");
      setBarcode("");
      setGST(DEFAULT_GST);
      setSaving(false);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!code.trim() || !desc.trim()) return toast.error("Enter code and description");
    setSaving(true);
    try {
      const part = code.trim();
      const aliasText = desc.trim();
      const payload = {
        "part no": part,
        alias: aliasText,
        "item name": part,
        brand: "",
        "group name": "",
        "cost price": rate ? `$${Number(rate).toFixed(2)}` : "$0.00",
        mrp: mrp ? `$${Number(mrp).toFixed(2)}` : "$0.00",
        "reorder level": 0,
        "min stock": 0,
        "max stock": 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, PRODUCT_MASTER_COLLECTION), payload);
      const newItem = normalizeProductMasterDoc({ id: ref.id, data: () => payload });
      onCreated?.(newItem);
      onClose();
      toast.success("Item created successfully");
    } catch (err) {
      console.error("Error creating item:", err);
      toast.error("Failed to create item");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded shadow-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold">Create Item</div>
          <button className="text-red-500" onClick={onClose} type="button">Close</button>
        </div>

        <div className="space-y-2 text-sm">
          <label><div className="text-xs">Item Code</div><input ref={codeRef} value={code} onChange={e => setCode(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">Description</div><input value={desc} onChange={e => setDesc(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">Unit</div><input value={unit} onChange={e => setUnit(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">Rate</div><input value={rate} onChange={e => setRate(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">MRP</div><input value={mrp} onChange={e => setMrp(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">Barcode</div><input value={barcode} onChange={e => setBarcode(e.target.value)} className="w-full border px-2 py-1" /></label>
          <label><div className="text-xs">GST %</div>
            <input
              type="number"
              value={gst}
              onChange={e => setGST(Number(e.target.value) || 0)}
              className="w-full border px-2 py-1"
            />
          </label>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 border rounded" type="button">Cancel</button>
          <button onClick={handleCreate} className="px-3 py-1 bg-blue-600 text-white rounded" disabled={saving} type="button">
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Adjust outward table column widths (px); persisted in localStorage. */
function ColumnWidthSettingsModal({
  open,
  onClose,
  columnLabels,
  draftWidths,
  setDraftWidths,
  onSave,
  onResetDefaults,
  voucherTypeLabel = "Outward Supply",
}) {
  if (!open) return null;
  const setOne = (idx, val) => {
    const n = Number(String(val).replace(/[^\d.]/g, ""));
    if (!Number.isFinite(n)) return;
    const w = Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, Math.round(n)));
    setDraftWidths((prev) => {
      const next = [...prev];
      next[idx] = w;
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 shrink-0">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-indigo-600">{voucherTypeLabel}</div>
            <div className="text-base font-bold text-slate-900">Column widths</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Width in pixels ({COL_WIDTH_MIN}–{COL_WIDTH_MAX}). Saved on this device only.
            </p>
          </div>
          <button type="button" className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-100" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3 space-y-2 text-xs">
          {columnLabels.map((label, idx) => (
            <label key={label} className="flex items-center gap-3">
              <span className="w-[40%] shrink-0 text-slate-600 font-medium truncate" title={label}>
                {label}
              </span>
              <input
                type="number"
                min={COL_WIDTH_MIN}
                max={COL_WIDTH_MAX}
                value={draftWidths[idx] ?? DEFAULT_BILLING_COL_WIDTHS[idx]}
                onChange={(e) => setOne(idx, e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 font-mono tabular-nums"
              />
              <span className="text-slate-400 w-8">px</span>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 shrink-0">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            onClick={onResetDefaults}
          >
            Reset defaults
          </button>
          <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 shadow-sm"
            onClick={onSave}
          >
            Apply &amp; save
          </button>
        </div>
      </div>
    </div>
  );
}

const ItemGridDropdown = React.memo(function ItemGridDropdown({
  items,
  loading,
  searchQuery,
  style,
  onSelect,
  highlightIndex,
  setHighlightIndex,
}) {
  return (
    <div
      id="item-grid-dropdown"
      className="bg-gray-200 border shadow-md rounded text-xs"
      style={{
        position: "absolute",
        zIndex: 9999,
        maxHeight: 300,
        overflow: "auto",
        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
        ...style,
      }}
    >
      <table className="w-full border-collapse">
        <thead className="bg-gray-200 sticky top-0">
          <tr>
            <th className="text-left px-2 py-1 text-[11px]">Part No.</th>
            <th className="text-left px-2 py-1 text-[11px]">Description</th>
            <th className="text-center px-2 py-1 text-[11px]">Brand</th>
            <th className="text-right px-2 py-1 text-[11px]">MRP</th>
            <th className="text-right px-2 py-1 text-[11px]">Stock</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="px-2 py-2 text-center text-gray-600">
                Searching Product Master…
              </td>
            </tr>
          ) : items && items.length ? (
            items.map((it, idx) => {
              const isHighlighted = idx === highlightIndex;
              return (
                <tr
                  key={it.id || `${it.code}-${idx}`}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onDoubleClick={() => onSelect(it)}
                  onClick={() => onSelect(it)}
                  className={`${isHighlighted ? "bg-blue-100" : "hover:bg-gray-50"} cursor-pointer`}
                >
                  <td className="px-2 py-1 truncate">{it.partno || it.code}</td>
                  <td className="px-2 py-1 truncate">{it.alias || it.desc}</td>
                  <td className="px-2 py-1 truncate">{it.brand}</td>
                  <td className="px-2 py-1 text-right">{(it.mrp ?? 0).toString()}</td>
                  <td className="px-2 py-1 text-right">{it.stock ?? ""}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={5} className="px-2 py-2 text-center text-gray-500">
                {searchQuery?.trim()
                  ? `No parts matching "${searchQuery.trim()}"`
                  : "Type part number or description — matches update as you type"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {items && items.length >= ITEM_DROPDOWN_MAX ? (
        <div className="border-t border-amber-200 bg-amber-50 px-2 py-1 text-[10px] text-amber-950">
          Showing the first {ITEM_DROPDOWN_MAX} matches — keep typing to narrow the list.
        </div>
      ) : null}
    </div>
  );
});

/* Main Component */
export default function BillingScreen({ voucherPageConfig }) {
  const pageConfig = voucherPageConfig ?? DEFAULT_OUTWARD_PAGE;
  const voucherTypeLabel = pageConfig.label;
  const isInward = pageConfig.variant === "inward";
  const partyGroup = pageConfig.partyGroup || "Sundry Debtors";
  const colWidthsKey = columnWidthsStorageKey(pageConfig.slug);
  const isTypeRow = useMemo(
    () => createExactVoucherTypeMatcher(voucherTypeLabel),
    [voucherTypeLabel]
  );
  const { user: authUser } = useAuth();
  const billingCreditBranches = useMemo(() => getVisibleAppBranches(authUser), [authUser]);
  const [creditOutletBranch, setCreditOutletBranchState] = useState("Main");

  useEffect(() => {
    const allowed = new Set(billingCreditBranches.map((b) => b.value));
    try {
      const stored = localStorage.getItem(BILLING_CREDIT_OUTLET_STORAGE_KEY);
      if (stored && allowed.has(stored)) {
        setCreditOutletBranchState(stored);
        return;
      }
    } catch {
      /* ignore */
    }
    setCreditOutletBranchState((prev) => (allowed.has(prev) ? prev : billingCreditBranches[0]?.value || "Main"));
  }, [billingCreditBranches]);

  const setCreditOutletBranch = (v) => {
    setCreditOutletBranchState(v);
    try {
      localStorage.setItem(BILLING_CREDIT_OUTLET_STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
  };

  const cols = [
    "Sl.", "Item Part No.", "Item Description", "Qty", "Unit", "Rate", "Gross Value",
    "Discount", "Dis. Amt", "Tax Class", "SGST Rate", "SGST Amount", "CGST Rate",
    "CGST Amount", "Amount", "Commission", "MRP"
  ];

  /* ---------------- state ---------------- */
  const [items, setItems] = useState([{ ...EMPTY_ROW_TEMPLATE }]);
  const [itemMaster, setItemMaster] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);

  const [transactionClasses, setTransactionClasses] = useState([]);
  const [transactionClass, setTransactionClass] = useState("");

  const [voucherNo, setVoucherNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [deliveryAddress, setDeliveryAddress] = useState({});
  const [cashCredit, setCashCredit] = useState("CASH");
  const [creditDueDate, setCreditDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  /** When checked, after a successful credit sale save, navigate to Credit / Due. */
  const [openCreditDueAfterSave, setOpenCreditDueAfterSave] = useState(false);
  const navigate = useNavigate();

  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceListLoading, setInvoiceListLoading] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  /** Opened from Find (sales import) — save back to lineChunks. */
  const [isImportedFindVoucher, setIsImportedFindVoucher] = useState(false);
  const [importSourceMeta, setImportSourceMeta] = useState(null);
  const [activeTab, setActiveTab] = useState("items");

  // feature states
  const [showQuickParty, setShowQuickParty] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemModalRow, setItemModalRow] = useState(null);
  const [isInterState, setIsInterState] = useState(false); // keep IGST toggle
  const [payment, setPayment] = useState({ mode: "Cash", amountPaid: 0, details: {} });

  // Packing & Forwarding state (new)
  const [packingAndForwarding, setPackingAndForwarding] = useState(0);

  const [saving, setSaving] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);

  const [columnWidths, setColumnWidths] = useState(() => loadBillingColumnWidths(colWidthsKey));
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [draftColWidths, setDraftColWidths] = useState(() => loadBillingColumnWidths(colWidthsKey));

  // Party balance state
  const [partyBalance, setPartyBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Discounts & commissions
  const cachedDiscountRulesRef = useRef([]); // cached active rules for selected party
  const [discountRulesLoading, setDiscountRulesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeCommissions, setEmployeeCommissions] = useState([]); // normalized array
  const [commissionTotals, setCommissionTotals] = useState({ total: 0 }); // { total }
  const invoiceListCacheRef = useRef({ ts: 0, list: [] });

  /* ---------------- refs ---------------- */
  const partyRef = useRef(null);
  const transRef = useRef(null);
  const cashRef = useRef(null);
  const codeRefs = useRef([]);
  const qtyRefs = useRef([]);
  const discRefs = useRef([]);

  const safeFocus = useCallback((ref, idx = 0) => {
    try {
      if (!ref) return;
      if (ref.current) {
        if (typeof ref.current.focus === "function") { ref.current.focus(); return; }
        if (Array.isArray(ref.current)) {
          const el = ref.current[idx];
          if (el && typeof el.focus === "function") el.focus();
          return;
        }
      }
      if (Array.isArray(ref) && ref[idx] && typeof ref[idx].focus === "function") {
        ref[idx].focus();
        return;
      }
    } catch (err) {
      console.error("safeFocus failed", err);
    }
  }, []);

  const itemSearchSeqRef = useRef(0);
  const itemCacheRef = useRef(new Map());
  const itemFetchInflightRef = useRef(new Map());
  const partyDiscountsAllRef = useRef(null);
  const skipCodeBlurRef = useRef(false);
  const partyBalanceDebounceRef = useRef(null);
  const commissionDebounceRef = useRef(null);

  /** No bulk catalog load (performance). Kept as no-op for any stale references. */
  const fetchItems = useCallback(async () => {}, []);

  const fetchItemByCodeFromFirestore = useCallback(async (rawCode) => {
    const code = String(rawCode ?? "").trim();
    if (!code) return null;
    const cached =
      lookupFromItemCache(itemCacheRef, code) ||
      lookupFromItemCache(itemCacheRef, code.toUpperCase()) ||
      lookupFromItemCache(itemCacheRef, code.toLowerCase());
    if (cached) return cached;

    const inflightKey = code.toLowerCase();
    if (itemFetchInflightRef.current.has(inflightKey)) {
      return itemFetchInflightRef.current.get(inflightKey);
    }

    const promise = (async () => {
      const it = await findProductMasterByPartNo(code);
      if (it) cacheItemInRef(itemCacheRef, it);
      return it;
    })();

    itemFetchInflightRef.current.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      itemFetchInflightRef.current.delete(inflightKey);
    }
  }, []);

  /** O(1) resolve from in-memory cache (no React re-render on search). */
  const itemLookupMap = useMemo(() => {
    const m = new Map(itemCacheRef.current);
    for (const it of itemMaster) registerItemInLookupMap(m, it);
    return m;
  }, [itemMaster]);

  /** Strict join map: import productId -> items.productid */
  const itemByProductIdMap = useMemo(() => {
    const m = new Map();
    const add = (it) => {
      const raw = String(it.productid ?? it.productId ?? "").trim();
      const norm = normalizeLookupKey(raw);
      if (raw) m.set(raw, it);
      if (norm) m.set(norm, it);
    };
    itemCacheRef.current.forEach(add);
    for (const it of itemMaster) add(it);
    return m;
  }, [itemMaster]);

  const fetchCustomers = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, "customers"), where("group", "==", partyGroup)));
      setPartyList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching parties:", err);
    }
  }, [partyGroup]);

  const fetchTransactionClasses = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "transactionClasses"));
      const arr = snap.docs.map(d => { const data = d.data(); return data.name || data.code || d.id || String(data); });
      setTransactionClasses(arr);
    } catch (err) { console.error("Error fetching transaction classes:", err); }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchTransactionClasses();
  }, [fetchCustomers, fetchTransactionClasses]);

  useEffect(() => {
    startProcessing({ intervalMs: 15000, continueOnError: true });

    const onOnline = () => {
      console.log("Back online - starting queue processing");
      startProcessing();
    };
    const onOffline = () => { console.log("Offline - queue processing paused"); };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      stopProcessing();
    };
  }, []);

  useEffect(() => {
    if (selectedParty) {
      setDeliveryAddress({
        partyName: selectedParty.partyName,
        address: selectedParty.address || "",
        gstin: selectedParty.gstin || "",
        phone: selectedParty.phone || "",
      });
      setTimeout(() => safeFocus(transRef), 40);
      (async () => {
        const rules = await fetchActiveDiscountRules(selectedParty);
        setItems(prev => applyDiscountsToItems(prev, rules));
      })();
    }
  }, [selectedParty]); // intentional: triggered when party changes

  /* ---------------- party balance helper & fetcher ---------------- */
  const formatCurrency = (v) => {
    try {
      const num = Number(v) || 0;
      return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
    } catch (e) {
      return "₹ " + (Number(v) || 0).toFixed(2);
    }
  };

  const fetchPartyBalance = useCallback(async (party) => {
    setBalanceLoading(true);
    try {
      if (!party) { setPartyBalance(0); setBalanceLoading(false); return; }
      const partyName = party.partyName || party.name || party.party || "";
      let totalDue = 0;
      let totalPaid = 0;

      if (party.id) {
        try {
          const snap = await getDocs(
            query(collection(db, COLLECTION_BILLS), where("customerId", "==", party.id), limit(500))
          );
          snap.docs.forEach((d) => {
            const b = d.data() || {};
            totalDue += Number(b.balanceAmount ?? b.totalAmount ?? 0) || 0;
            totalPaid += Number(b.paidAmount ?? 0) || 0;
          });
        } catch (e) {
          console.warn("fetchPartyBalance creditBills failed", e);
        }
      }

      const balance = Number(totalDue) || 0;
      setPartyBalance(balance);
    } catch (err) {
      console.error("fetchPartyBalance failed:", err);
      setPartyBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (partyBalanceDebounceRef.current) clearTimeout(partyBalanceDebounceRef.current);
    if (!selectedParty) {
      setPartyBalance(0);
      return undefined;
    }
    partyBalanceDebounceRef.current = setTimeout(() => {
      fetchPartyBalance(selectedParty);
    }, 400);
    return () => {
      if (partyBalanceDebounceRef.current) clearTimeout(partyBalanceDebounceRef.current);
    };
  }, [selectedParty, fetchPartyBalance]);

  /* ---------------- discounts & commissions helpers ---------------- */
  const fetchActiveDiscountRules = useCallback(async (party) => {
    setDiscountRulesLoading(true);
    try {
      if (!party) {
        cachedDiscountRulesRef.current = [];
        setDiscountRulesLoading(false);
        return [];
      }
      if (!partyDiscountsAllRef.current) {
        const allSnap = await getDocs(collection(db, "partyDiscounts"));
        partyDiscountsAllRef.current = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      const all = partyDiscountsAllRef.current;
      const todayStr = new Date().toISOString().slice(0, 10);

      const filtered = all.filter(r => {
        if (r.partyId && String(r.partyId) !== String(party.id)) return false;
        if (r.effectiveFrom && r.effectiveFrom > todayStr) return false;
        if (r.effectiveTo && r.effectiveTo < todayStr) return false;
        return true;
      });
      cachedDiscountRulesRef.current = filtered;
      setDiscountRulesLoading(false);
      return filtered;
    } catch (e) {
      console.warn("fetchActiveDiscountRules failed", e);
      cachedDiscountRulesRef.current = [];
      setDiscountRulesLoading(false);
      return [];
    }
  }, []);

  const applyDiscountsToItems = useCallback((itemsArr, rules) => {
    if (!Array.isArray(itemsArr)) return itemsArr;
    if (!Array.isArray(rules) || rules.length === 0) return itemsArr.map(i => ({ ...i }));
    return itemsArr.map((it) => {
      const copy = { ...it };
      const score = (r) => ((r.partyId ? 2 : 0) + (r.brand ? 1 : 0));
      const matched = rules.filter(r => {
        if (r.brand) {
          const itBrand = (it.brand || it.desc || "").toString().toLowerCase();
          if (itBrand.indexOf(String(r.brand || "").toLowerCase()) === -1) return false;
        }
        return true;
      });
      if (!matched.length) return copy;
      matched.sort((a, b) => score(b) - score(a));
      const chosen = matched[0];

      let base = 0;
      if (chosen.priceSource === "mrp") base = Number(it.mrp || it.mrp || it.rate || 0);
      else if (chosen.priceSource === "cost") base = Number(it.cost || 0);
      else base = Number(it.rate || 0);

      if (chosen.type === "percent") {
        copy.appliedDiscount = { type: "percent", value: Number(chosen.value || 0), ruleId: chosen.id };
        copy.discount = Number(chosen.value || 0);
        delete copy.appliedDiscountAmount;
      } else {
        copy.appliedDiscount = { type: "amount", value: Number(chosen.value || 0), ruleId: chosen.id };
        copy.appliedDiscountAmount = Number(chosen.value || 0);
        const baseRate = base || Number(it.rate || 0);
        copy.discount = baseRate > 0 ? (Number(chosen.value || 0) / baseRate) * 100 : 0;
      }
      return copy;
    });
  }, []);

  const fetchEmployeeCommissions = useCallback(async (empParty) => {
    if (!empParty) { setEmployeeCommissions([]); return []; }
    try {
      const q = query(collection(db, "employeeCommissions"), where("partyId", "==", empParty.id), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const doc0 = snap.docs[0].data();
        const items = doc0.items || doc0.commissions || [];
        const normalized = (items || []).map(it => ({
          code: (it.code || it.itemCode || "").toString(),
          ratePercent: Number(it.ratePercent ?? it.rate ?? 0),
          amount: Number(it.amount ?? it.fixedAmount ?? 0),
        }));
        setEmployeeCommissions(normalized);
        return normalized;
      } else {
        setEmployeeCommissions([]);
        return [];
      }
    } catch (e) {
      console.warn("fetchEmployeeCommissions failed", e);
      setEmployeeCommissions([]);
      return [];
    }
  }, []);

  /* ---------------- commission calculation (defensive) ---------------- */
  const itemsKey = useMemo(() => {
    return (items || []).map(it =>
      `${it.code || ""}::${it.qty || ""}::${it.rate || ""}::${it.discount || ""}`
    ).join("|");
  }, [items]);

  const lastComputedKeyRef = useRef(null);
  const recomputeRunningRef = useRef(false);

  // Normalize commission numeric from many possible keys
  const _getCommissionFromReturned = (ret) => {
    if (!ret) return 0;
    if (typeof ret === "number") return ret;
    if (typeof ret === "string" && !isNaN(Number(ret))) return Number(ret);
    const candidates = [
      ret.commissionAmount, ret.commission_amount, ret.commission,
      ret.commissionAmt, ret.commAmount, ret.comm_amt, ret.amount, ret.commission_amount_inr
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && c !== "") {
        const n = Number(c);
        if (!isNaN(n)) return n;
      }
    }
    return 0;
  };

  // Try to extract an array or map from the helper result
  const _extractReturnedItems = (res) => {
    if (!res) return { itemsArray: [], itemsMap: new Map(), total: 0 };
    const total = Number(res.total ?? res.totals ?? 0) || 0;

    if (Array.isArray(res.items)) return { itemsArray: res.items, itemsMap: new Map(), total };
    if (Array.isArray(res)) return { itemsArray: res, itemsMap: new Map(), total: 0 };

    if (res.items && typeof res.items === "object") {
      const itemsMap = new Map();
      Object.keys(res.items).forEach(k => itemsMap.set(String(k).trim(), res.items[k]));
      return { itemsArray: [], itemsMap, total };
    }

    const possibleMaps = ["byCode", "by_code", "map", "itemsByCode", "items_map", "by_item"];
    for (const key of possibleMaps) {
      if (res[key] && typeof res[key] === "object") {
        const itemsMap = new Map();
        Object.keys(res[key]).forEach(k => itemsMap.set(String(k).trim(), res[key][k]));
        return { itemsArray: [], itemsMap, total };
      }
    }

    if (typeof res === "object") {
      const keys = Object.keys(res);
      const plausibleCodes = keys.filter(k => typeof res[k] === "object" || typeof res[k] === "number");
      if (plausibleCodes.length > 0 && (plausibleCodes.length / Math.max(1, keys.length)) > 0.3) {
        const itemsMap = new Map();
        plausibleCodes.forEach(k => itemsMap.set(String(k).trim(), res[k]));
        return { itemsArray: [], itemsMap, total };
      }
    }

    return { itemsArray: [], itemsMap: new Map(), total };
  };

  useEffect(() => {
    if (!selectedEmployee?.id) return undefined;
    let mounted = true;

    if (commissionDebounceRef.current) clearTimeout(commissionDebounceRef.current);
    commissionDebounceRef.current = setTimeout(() => {
    (async () => {
      try {
        const empId = selectedEmployee?.id || null;
        const key = `${itemsKey}||EMP:${empId}`;

        if (lastComputedKeyRef.current === key) return;
        if (recomputeRunningRef.current) return;
        if (typeof computeCommissionsForSave !== "function") {
          console.warn("[comm] computeCommissionsForSave not a function");
          return;
        }

        recomputeRunningRef.current = true;

        let res = null;
        try {
          res = await computeCommissionsForSave(items, empId);
        } catch (err) {
          console.error("[comm] computeCommissionsForSave threw:", err);
          res = null;
        }

        if (!mounted) return;

        const { itemsArray, itemsMap, total } = _extractReturnedItems(res);

        const mapFullKey = new Map();
        const mapByCodeFirst = new Map();
        if (itemsArray && itemsArray.length) {
          itemsArray.forEach((ri, idx) => {
            const code = (ri.code ?? ri.itemCode ?? ri.item_code ?? "").toString().trim();
            const qty = ri.qty ?? ri.q ?? ri.quantity ?? ri.qtyPack ?? "";
            const rate = ri.rate ?? ri.price ?? ri.unitPrice ?? ri.unit_price ?? "";
            const keyFull = `${code}::${qty}::${rate}`;
            mapFullKey.set(keyFull, ri);
            if (code && !mapByCodeFirst.has(code)) mapByCodeFirst.set(code, ri);
          });
        }
        const mapByCode = new Map(mapByCodeFirst);
        if (itemsMap && itemsMap.size) {
          for (const [k, v] of itemsMap.entries()) {
            if (!mapByCode.has(k)) mapByCode.set(k, v);
          }
        }

        setItems(prevItems => {
          const newItems = prevItems.map((orig, idx) => {
            const origCode = (orig.code || "").toString().trim();
            const origQty = orig.qty ?? "";
            const origRate = orig.rate ?? orig.unitPrice ?? "";

            const keyFull = `${origCode}::${origQty}::${origRate}`;
            let matched = mapFullKey.get(keyFull);

            if (!matched && origCode) matched = mapByCode.get(origCode);

            if (!matched && itemsArray && itemsArray.length && itemsArray[idx]) matched = itemsArray[idx];

            if (!matched && itemsMap && itemsMap.size) {
              const arrFromMap = Array.from(itemsMap.values());
              if (arrFromMap[idx]) matched = arrFromMap[idx];
            }

            const commissionAmount = _getCommissionFromReturned(matched);
            return { ...orig, commissionAmount: Number(commissionAmount || 0) };
          });

          let changed = false;
          if (newItems.length !== prevItems.length) changed = true;
          else {
            for (let i = 0; i < newItems.length; i++) {
              if (Number(newItems[i].commissionAmount || 0) !== Number(prevItems[i]?.commissionAmount || 0)) { changed = true; break; }
            }
          }
          if (changed) return newItems;
          return prevItems;
        });

        setCommissionTotals({ total: Number(total || 0) });

        lastComputedKeyRef.current = key;
      } catch (err) {
        console.error("[comm] recompute effect failed:", err);
      } finally {
        recomputeRunningRef.current = false;
      }
    })();
    }, COMMISSION_RECOMPUTE_DEBOUNCE_MS);

    return () => {
      mounted = false;
      if (commissionDebounceRef.current) clearTimeout(commissionDebounceRef.current);
    };
  }, [itemsKey, selectedEmployee?.id, items]);
  // ---- Fallback commission calculator (uses employeeCommissions) ----
  const computeFromLocalRules = (itemsArr, rulesArr) => {
    const map = new Map();
    (rulesArr || []).forEach(r => {
      const code = String(r.code || r.itemCode || "").trim();
      if (!code) return;
      map.set(code, {
        ratePercent: Number(r.ratePercent ?? r.rate ?? 0) || 0,
        amount: Number(r.amount ?? r.fixedAmount ?? 0) || 0,
      });
    });

    let total = 0;
    const out = (itemsArr || []).map(it => {
      const code = String(it.code || it.itemCode || "").trim();
      const qty = Number(it.qty || 0) || 0;
      const rate = Number(it.rate || it.unitPrice || 0) || 0;

      const rule = map.get(code);
      let comm = 0;
      if (rule) {
        if (rule.ratePercent) comm += (qty * rate * rule.ratePercent) / 100;
        if (rule.amount) comm += (qty * rule.amount);
      }
      total += comm;
      return { ...it, commissionAmount: Number(comm || 0) };
    });

    return { items: out, total: Number(total || 0) };
  };


  /* ---------------- row calc / totals ---------------- */
  const calcRow = useCallback((row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    const disc = parseFloat(row.discount) || 0;
    const gstVal = row?.gst ?? row?.GST ?? DEFAULT_GST;
    const gst = parseFloat(gstVal) || DEFAULT_GST;
    const gross = qty * rate;
    const discAmt = (gross * disc) / 100;
    const netBeforeTax = gross - discAmt;

    if (isInterState) {
      const igstAmt = (netBeforeTax * gst) / 100;
      const netAmount = netBeforeTax + igstAmt;
      return { gross, discAmt, sgstAmt: 0, cgstAmt: 0, igstAmt, netAmount };
    } else {
      const half = gst / 2;
      const sgstAmt = (netBeforeTax * half) / 100;
      const cgstAmt = sgstAmt;
      const netAmount = netBeforeTax + sgstAmt + cgstAmt;
      return { gross, discAmt, sgstAmt, cgstAmt, igstAmt: 0, netAmount };
    }
  }, [isInterState]);

  const totals = useMemo(() => {
    return items.reduce((acc, r) => {
      const { gross, discAmt, sgstAmt = 0, cgstAmt = 0, igstAmt = 0, netAmount } = calcRow(r);
      acc.qty += Number(r.qty || 0);
      acc.gross += gross;
      acc.discount += discAmt;
      acc.sgst += sgstAmt;
      acc.cgst += cgstAmt;
      acc.igst += igstAmt;
      acc.net += netAmount;
      return acc;
    }, { qty: 0, gross: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, net: 0 });
  }, [items, calcRow]);

  const pfAmount = Number(packingAndForwarding) || 0;
  const grandTotalWithPF = totals.net + pfAmount;
  const grandTotalRounded = Math.round(grandTotalWithPF);
  const roundOff = Number((grandTotalRounded - grandTotalWithPF).toFixed(2));
  const grandTotalDisplay = Number(grandTotalRounded).toFixed(2);

  const rowValidation = useMemo(() => {
    return items.map((r) => {
      const errors = [];
      if (!r.code || String(r.code).trim() === "") errors.push("code");
      if (!Number(r.qty) || Number(r.qty) <= 0) errors.push("qty");
      return { ok: errors.length === 0, errors };
    });
  }, [items]);

  /* ---------------- CRUD ---------------- */
  const newInvoice = useCallback(() => {
    setItems([{ ...EMPTY_ROW_TEMPLATE }]);
    setCodeDropdown((prev) => ({ ...prev, open: false, row: null }));
    setFilteredItems([]);
    setHighlightIndex(0);
    setCurrentInvoiceId(null);
    setIsImportedFindVoucher(false);
    setImportSourceMeta(null);
    setVoucherNo("");
    setSelectedParty(null);
    setDeliveryAddress({});
    setTransactionClass("");
    setCashCredit("CASH");
    setOpenCreditDueAfterSave(false);
    {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      setCreditDueDate(d.toISOString().slice(0, 10));
    }
    setInvoiceDate(new Date().toISOString().split("T")[0]);
    setPackingAndForwarding(0);
    setShowInvoiceList(false);
    setSelectedEmployee(null);
    setEmployeeCommissions([]);
    setCommissionTotals({ total: 0 });
    setTimeout(() => safeFocus(codeRefs, 0), 40);
  }, [safeFocus]);

  const handleSave = useCallback(async () => {
    if (!selectedParty) return toast.error("Please select a party");
    const invalid = rowValidation.some(v => !v.ok);
    if (invalid) { toast.error("There are invalid rows. Fix highlighted rows before saving."); return; }
    if (!items.some((it) => it.code && Number(it.qty) > 0)) return toast.error("Please enter at least one item");

    let itemsWithCommission = items;
    let commissionTotal = 0;
    try {
      const result = await computeCommissionsForSave(items, selectedEmployee?.id);
      if (result && Array.isArray(result.items)) {
        itemsWithCommission = result.items;
        commissionTotal = result.total || 0;
      }
      setCommissionTotals({ total: commissionTotal });
    } catch (e) {
      console.warn("computeCommissionsForSave failed on save:", e);
    }

    if (!invoiceDate) {
      toast.error("Please set the voucher date before saving.");
      return;
    }

    let saveVoucherNo = String(voucherNo || "").trim();
    if (!saveVoucherNo) {
      saveVoucherNo = generateBillingVoucherNo();
      setVoucherNo(saveVoucherNo);
    }

    const itemsForSave = [];
    for (const it of itemsWithCommission) {
      const code = String(it.code || "").trim();
      const qty = Number(it.qty) || 0;
      if (!code || qty <= 0) {
        itemsForSave.push(it);
        continue;
      }
      if (productIdFromItem(it)) {
        itemsForSave.push(it);
        continue;
      }
      const found = await fetchItemByCodeFromFirestore(code);
      if (found && productIdFromItem(found)) {
        itemsForSave.push({
          ...it,
          desc: String(it.desc || found.alias || found.desc || "").trim(),
          productId: String(found.productid ?? found.productId),
          productid: found.productid ?? found.productId,
        });
      } else {
        itemsForSave.push(it);
      }
    }

    const missingPid = itemsForSave.filter(
      (it) => String(it.code || "").trim() && Number(it.qty) > 0 && !productIdFromItem(it)
    );
    if (missingPid.length) {
      toast.error(
        "Each line needs a Product Master match (Product ID). Pick the part from the dropdown or press Enter after a valid part number."
      );
      return;
    }

    const partySnapshot = selectedParty;
    try {
      setSaving(true);
        const result = await saveBillingFindVoucher({
          voucherNo: saveVoucherNo,
          items: itemsForSave,
          customerName: selectedParty?.partyName || "",
          invoiceDate,
          sourceRows: importSourceMeta?.sourceRows || [],
          sourceTemplateRow: importSourceMeta?.sourceTemplateRow || null,
          year: importSourceMeta?.year,
          monthIndex: importSourceMeta?.monthIndex,
          weekOfMonth: importSourceMeta?.weekOfMonth,
          periodKey: importSourceMeta?.periodKey || "",
          voucherType: voucherTypeLabel,
          isTypeRow,
          employeeName:
            selectedEmployee?.partyName || selectedEmployee?.name || selectedEmployee?.displayName || "",
        });
        invoiceListCacheRef.current = { ts: 0, list: [] };
      setIsImportedFindVoucher(false);
      setImportSourceMeta(null);

      if (!isInward && cashCredit === "CREDIT" && partySnapshot?.id) {
        try {
          await createCreditBillFromInvoice({
            invoiceId: null,
            voucherNo: voucherNo || "",
            customerId: partySnapshot.id,
            customerName: partySnapshot.partyName || "",
            phone: partySnapshot.phone || deliveryAddress?.phone || "",
            totalAmount: grandTotalRounded,
            dueDate: creditDueDate || null,
            invoiceDate,
            branch: creditOutletBranch,
          });
        } catch (credErr) {
          console.error("Credit bill row failed:", credErr);
          toast.error("Import saved; credit due row failed — check Credit / Due page.");
        }
      }

      try { fetchPartyBalance(selectedParty); } catch (e) { /* ignore */ }

      setItems([{ ...EMPTY_ROW_TEMPLATE }]);
      setSelectedParty(null);
      setDeliveryAddress({});
      setInvoiceDate(new Date().toISOString().split("T")[0]);
      setPackingAndForwarding(0);
      setSelectedEmployee(null);
      setEmployeeCommissions([]);
      setCommissionTotals({ total: 0 });
      setTimeout(() => safeFocus(codeRefs, 0), 40);
      toast.success(`Saved to ${result.label}`);
      if (!isInward && cashCredit === "CREDIT" && openCreditDueAfterSave) {
        navigate("/credit-due");
      }
      return { ok: true, source: "import", result };
    } catch (err) {
      console.error("saveBillingFindVoucher failed:", err);
      toast.error(err?.message || "Failed to save to import data");
      return { ok: false, error: String(err) };
    } finally {
      setSaving(false);
    }
  }, [selectedParty, rowValidation, items, invoiceDate, deliveryAddress, transactionClass, cashCredit, creditDueDate, openCreditDueAfterSave, totals, roundOff, grandTotalRounded, isInterState, payment, voucherNo, safeFocus, packingAndForwarding, fetchPartyBalance, selectedEmployee, navigate, importSourceMeta, isImportedFindVoucher, creditOutletBranch, voucherTypeLabel, isTypeRow, isInward, fetchItemByCodeFromFirestore]);

  const modifyInvoice = useCallback(async () => {
    return handleSave();
  }, [handleSave]);

  const deleteInvoice = useCallback(async () => {
    const vn = String(voucherNo || "").trim();
    if (!vn) {
      newInvoice();
      toast.success("Screen cleared.");
      return;
    }

    const ok = window.confirm(
      `Delete all ${voucherTypeLabel} line(s) for voucher ${vn} from sales_import_months?\n\nThis cannot be undone.`
    );
    if (!ok) return;

    if (!invoiceDate) {
      toast.error("Set the voucher date so the correct import week can be found.");
      return;
    }

    try {
      setSaving(true);
      const result = await deleteBillingFindVoucher({
        voucherNo: vn,
        invoiceDate,
        sourceRows: importSourceMeta?.sourceRows || [],
        year: importSourceMeta?.year,
        monthIndex: importSourceMeta?.monthIndex,
        weekOfMonth: importSourceMeta?.weekOfMonth,
        periodKey: importSourceMeta?.periodKey || "",
        voucherType: voucherTypeLabel,
        isTypeRow,
      });
      invoiceListCacheRef.current = { ts: 0, list: [] };
      setIsImportedFindVoucher(false);
      setImportSourceMeta(null);
      newInvoice();
      toast.success(
        `Deleted ${result.removed} line(s) for voucher ${vn} from sales import.`
      );
    } catch (err) {
      console.error("deleteBillingFindVoucher failed:", err);
      toast.error(err?.message || "Failed to delete from sales import");
    } finally {
      setSaving(false);
    }
  }, [
    voucherNo,
    voucherTypeLabel,
    invoiceDate,
    importSourceMeta,
    isTypeRow,
    newInvoice,
  ]);

  const loadInvoiceList = useCallback(async (force = false) => {
    try {
      const nowMs = Date.now();
      const cache = invoiceListCacheRef.current;
      if (
        !force &&
        cache?.list?.length &&
        nowMs - (cache.ts || 0) < FIND_CACHE_TTL_MS
      ) {
        setInvoiceList(cache.list);
        return;
      }
      setInvoiceListLoading(true);
      const lookupMap = itemByProductIdMap;

      const grouped = new Map();

      const addOutwardRow = (row) => {
        const norm = {};
        Object.keys(row || {}).forEach((k) => {
          const nk = String(k).toLowerCase().replace(/[\s_]/g, "");
          norm[nk] = row[k];
        });

        const voucherNo = String(
          row.voucherNo ??
            row.VoucherNo ??
            row.voucherno ??
            row["Voucher No"] ??
            norm.voucherno ??
            row.invoiceNo ??
            row.InvoiceNo ??
            norm.invoiceno ??
            ""
        ).trim();
        if (!voucherNo) return;

        const periodKey = String(row.periodKey || "").trim();
        const groupKey = periodKey ? `${voucherNo}::${periodKey}` : voucherNo;

        if (!grouped.has(groupKey)) {
          grouped.set(groupKey, {
            id: groupKey,
            voucherNo,
            type: "outward",
            status: "imported",
            isImportedPreview: true,
            party:
              String(
                row.customerName ??
                  row.party ??
                  row.partyName ??
                  row.PartyName ??
                  row.accountName ??
                  row.AccountName ??
                  ""
              ).trim() || "",
            date:
              row.tranDate ??
              row.voucherDate ??
              row.VoucherDate ??
              row.date ??
              row.Date ??
              row.invoiceDate ??
              row.InvoiceDate ??
              null,
            createdByName: "Sales Import",
            importSource: "sales_import",
            importPeriodKey: "",
            employeeName: "",
            lineCount: 0,
            items: [],
            totals: { net: 0 },
            grandTotal: 0,
          });
        }

        const inv = grouped.get(groupKey);
        const pidRaw = String(
          row.productId ?? row.ProductId ?? row.productid ?? row["Product Id"] ?? norm.productid ?? ""
        ).trim();
        const pid = normalizeLookupKey(pidRaw);
        const itm = (pidRaw && lookupMap.get(pidRaw)) || (pid && lookupMap.get(pid)) || null;
        const qty =
          Number(row.qty ?? row.quantity ?? row.Qty ?? row["Qty"] ?? norm.qty ?? norm.quantity ?? 0) || 0;
        const rate = parseMoneyLike(
          row.unitPrice ??
            row.rate ??
            row.Rate ??
            row.salesRate ??
            row.unitprice ??
            row.UnitPrice ??
            norm.unitprice ??
            norm.rate ??
            0
        );
        const amount =
          parseMoneyLike(
            row.amount ??
              row.Amount ??
              row.lineAmount ??
              row.LineAmount ??
              norm.amount ??
              norm.lineamount ??
              (qty && rate ? qty * rate : 0)
          ) || 0;
        const gst = Number(row.gst ?? row.GST ?? itm?.gst ?? DEFAULT_GST) || DEFAULT_GST;

        const line = {
          productId: pidRaw || pid || undefined,
          code:
            String(itm?.partno ?? "").trim() ||
            String(itm?.code ?? "").trim() ||
            String(row.partNo ?? row.code ?? row.itemCode ?? row.Code ?? "").trim(),
          desc:
            String(itm?.alias ?? "").trim() ||
            String(row.alias ?? row.itemName ?? row.desc ?? "").trim(),
          qty,
          unit: resolveUnit(itm?.unit || row.unit || row.Unit),
          rate,
          discount: Number(row.discount ?? row.discountAmt ?? row.Discount ?? 0) || 0,
          gst,
          mrp: parseMoneyLike(row.mrp ?? row.MRP ?? norm.mrp ?? itm?.mrp ?? 0) || 0,
          commissionAmount: 0,
        };

        if (!inv.importPeriodKey && row.periodKey) inv.importPeriodKey = String(row.periodKey);
        if (!inv.employeeName && row.employeeName) {
          inv.employeeName = String(row.employeeName).trim();
        }
        if (!inv.sourceRows) inv.sourceRows = [];
        inv.sourceRows.push(row);
        if (!inv.sourceTemplateRow) inv.sourceTemplateRow = row;
        if (!inv.year && row.year) inv.year = Number(row.year);
        if (inv.monthIndex == null && row.month != null) inv.monthIndex = Number(row.month) - 1;
        if (!inv.weekOfMonth && row.weekOfMonth) inv.weekOfMonth = Number(row.weekOfMonth);
        inv.items.push(line);
        inv.lineCount = inv.items.length;
        inv.totals.net += amount;
        inv.grandTotal += amount;
      };

      await forEachVoucherTypeRowFromSalesImport((row) => addOutwardRow(row), voucherTypeLabel, { maxWeeks: 8 });

      const listOutward = Array.from(grouped.values()).sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const dbv = b.date ? new Date(b.date).getTime() : 0;
        return dbv - da;
      });

      setInvoiceList(listOutward);
      invoiceListCacheRef.current = { ts: Date.now(), list: listOutward };

      if (!listOutward.length) {
        toast(
          `No ${voucherTypeLabel} vouchers found. Upload weekly sales data in Import Center.`,
          { duration: 6000 }
        );
      }
    } catch (err) {
      console.error("loadInvoiceList failed:", err);
      const msg = String(err?.message || err?.code || "").replace(/^Firebase:\s*/i, "").trim();
      toast.error(msg ? `Failed to load ${voucherTypeLabel} vouchers: ${msg}` : `Failed to load ${voucherTypeLabel} vouchers`);
    } finally {
      setInvoiceListLoading(false);
    }
  }, [itemByProductIdMap, voucherTypeLabel]);

  const loadInvoice = (inv) => {
    const normalizedItems = (inv.items && inv.items.length) ? inv.items.map(it => ({
      ...it,
      // Item Code must come from items.partno
      code: String(
        itemByProductIdMap.get(normalizeLookupKey(it.productId ?? it.productid ?? ""))?.partno ?? it.code ?? ""
      ).trim(),
      // Re-resolve description at open time: Item Description must be items.alias
      desc: String(
        itemByProductIdMap.get(normalizeLookupKey(it.productId ?? it.productid ?? ""))?.alias ?? it.desc ?? ""
      ).trim(),
      gst: it.gst ?? it.GST ?? DEFAULT_GST,
      unit: resolveUnit(it.unit),
      // normalize commission amount to a number from multiple possible stored keys
      commissionAmount: Number(it.commissionAmount ?? it.commission_amount ?? it.commission ?? it.commissionAmt ?? 0)
    })) : [{ ...EMPTY_ROW_TEMPLATE }];

    setItems(normalizedItems);
    // set commission total if present
    const invCommissionTotal = Number(inv.commission?.total ?? inv.commissionTotal ?? inv.commission_total ?? 0);
    setCommissionTotals({ total: invCommissionTotal || 0 });

    const importedPreview =
      inv.isImportedPreview === true ||
      inv.status === "imported" ||
      inv.importSource === "sales_import" ||
      String(inv.createdByName || "").includes("Sales Import");
    setIsImportedFindVoucher(importedPreview);
    setImportSourceMeta(
      importedPreview
        ? {
            source: "sales_import",
            voucherNo: inv.voucherNo || inv.voucher_no || inv.voucher || "",
            periodKey: inv.importPeriodKey || "",
            year: inv.year,
            monthIndex: inv.monthIndex,
            weekOfMonth: inv.weekOfMonth,
            sourceRows: inv.sourceRows || [],
            sourceTemplateRow: inv.sourceTemplateRow || null,
          }
        : null
    );
    setCurrentInvoiceId(importedPreview ? null : inv.id || inv._id || null);
    setVoucherNo(inv.voucherNo || inv.voucher_no || inv.voucher || "");
    if (inv.date) {
      try {
        const dt = (typeof inv.date === "object" && typeof inv.date.toDate === "function") ? inv.date.toDate() : new Date(inv.date);
        setInvoiceDate(!isNaN(dt.getTime()) ? dt.toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
      } catch (err) { setInvoiceDate(new Date().toISOString().split("T")[0]); }
    } else setInvoiceDate("");
    if (inv.partyDetails && typeof inv.partyDetails === "object" && Object.keys(inv.partyDetails).length > 0) {
      setSelectedParty(inv.partyDetails);
    } else if (inv.party) {
      const found = partyList.find(p => p.partyName === inv.party || p.name === inv.party || p.id === inv.party);
      if (found) setSelectedParty(found);
      else setSelectedParty({ partyName: inv.party });
    } else if (inv.selectedParty && typeof inv.selectedParty === "object") {
      setSelectedParty(inv.selectedParty);
    } else {
      setSelectedParty(null);
    }
    setDeliveryAddress(inv.deliveryAddress || {});
    if (inv.transactionClass) setTransactionClass(inv.transactionClass);
    if (inv.cashCredit) setCashCredit(inv.cashCredit);
    if (typeof inv.isInterState === "boolean") setIsInterState(inv.isInterState);
    if (inv.payment) setPayment(inv.payment);
    setPackingAndForwarding(Number(inv.packingAndForwarding) || 0);
    if (inv.commission && inv.commission.employeePartyId) {
      const emp = partyList.find(p => p.id === inv.commission.employeePartyId);
      setSelectedEmployee(emp || null);
      if (emp) fetchEmployeeCommissions(emp);
    } else {
      setSelectedEmployee(null);
      setEmployeeCommissions([]);
    }

    setShowInvoiceList(false);
    setTimeout(() => safeFocus(codeRefs, 0), 40);
  };

  // If invoice is opened before items finish loading, backfill code/desc from item master.
  useEffect(() => {
    if (!currentInvoiceId) return;
    if (!itemByProductIdMap || itemByProductIdMap.size === 0) return;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((it) => {
        const key = normalizeLookupKey(it.productId ?? it.productid ?? "");
        if (!key) return it;
        const matched = itemByProductIdMap.get(key);
        if (!matched) return it;
        const nextCode = String(matched.partno ?? matched.code ?? it.code ?? "").trim();
        const nextDesc = String(matched.alias ?? it.desc ?? "").trim();
        if (nextCode === String(it.code ?? "").trim() && nextDesc === String(it.desc ?? "").trim()) {
          return it;
        }
        changed = true;
        return { ...it, code: nextCode, desc: nextDesc };
      });
      return changed ? next : prev;
    });
  }, [itemByProductIdMap, currentInvoiceId]);

  /* ---------------- item select & keyboard ---------------- */
  const handleChange = (i, field, value) => {
    setItems(prev => {
      const copy = [...prev];
      copy[i] = { ...copy[i], [field]: value };
      return copy;
    });
  };

  const mergeItemIntoMaster = useCallback((itm) => {
    if (!itm?.id && !itm?.partno && !itm?.code) return;
    cacheItemInRef(itemCacheRef, itm);
    setItemMaster((prev) => {
      const key = String(itm.id || itm.partno || itm.code).toLowerCase();
      if (prev.some((x) => String(x.id || x.partno || x.code).toLowerCase() === key)) return prev;
      const next = [...prev, itm];
      if (next.length > ITEM_CACHE_MAX) return next.slice(-ITEM_CACHE_MAX);
      return next;
    });
  }, []);

  const searchItemsForDropdown = useCallback(async (q) => {
    const ql = (q || "").toString().trim();
    const seq = ++itemSearchSeqRef.current;

    const applyList = (list) => {
      if (seq !== itemSearchSeqRef.current) return;
      setFilteredItems(list.slice(0, ITEM_DROPDOWN_MAX));
      setHighlightIndex(0);
    };
    const endSearch = () => {
      if (seq !== itemSearchSeqRef.current) return;
      setItemSearchLoading(false);
    };

    if (!ql) {
      applyList(filterAndSortCachedProducts(itemCacheRef.current, "", ITEM_EMPTY_PREVIEW));
      endSearch();
      return;
    }

    const local = filterAndSortCachedProducts(itemCacheRef.current, ql, ITEM_DROPDOWN_MAX);
    applyList(local);
    if (local.length >= ITEM_DROPDOWN_MAX) {
      endSearch();
      return;
    }

    try {
      const remote = await queryProductMasterByPartPrefix(ql, ITEM_DROPDOWN_MAX);
      if (seq !== itemSearchSeqRef.current) return;
      remote.forEach((it) => cacheItemInRef(itemCacheRef, it));
      const qlLower = ql.toLowerCase();
      const merged = dedupeProductList([...local, ...remote]);
      merged.sort((a, b) => {
        const rank = (it) => {
          const p = String(it.partno || it.code || "").toLowerCase();
          if (p.startsWith(qlLower)) return 0;
          if (p.includes(qlLower)) return 1;
          return 2;
        };
        return rank(a) - rank(b) || String(a.partno || "").localeCompare(String(b.partno || ""));
      });
      applyList(merged);
    } catch (e) {
      console.warn("searchItemsForDropdown failed:", e);
      applyList(local);
    } finally {
      endSearch();
    }
  }, []);

  const applyItemToRow = useCallback(
    (i, itm) => {
      const itemCode = String(itm.partno || itm.code || "").trim();
      const itemDesc = String(itm.alias || itm.desc || "").trim();
      const saleMrp = Number(itm.mrp) || 0;
      const lineRate =
        saleMrp > 0
          ? String((saleMrp * MRP_TO_RATE_MULTIPLIER).toFixed(2))
          : String(itm.rate ?? "");
      const pid = itm.productid ?? itm.productId ?? "";
      setItems((prev) => {
        const copy = [...prev];
        copy[i] = {
          ...copy[i],
          code: itemCode,
          desc: itemDesc,
          unit: resolveUnit(itm.unit),
          rate: lineRate,
          mrp: saleMrp || itm.mrp,
          gst: itm.gst ?? itm.GST ?? DEFAULT_GST,
          stock: itm.stock || 0,
          brand: itm.brand || itm.brandName || "",
          productId: pid !== "" && pid != null ? String(pid) : "",
          productid: pid !== "" && pid != null ? pid : "",
        };
        return applyDiscountsToItems(copy, cachedDiscountRulesRef.current || []);
      });
      setTimeout(() => safeFocus(qtyRefs, i), 60);
    },
    [applyDiscountsToItems, safeFocus]
  );

  const selectItem = useCallback(
    async (i, codeOrValue) => {
      if (!codeOrValue) return;
      const code = String(codeOrValue).trim();
      const key = code.toLowerCase();
      let itm = lookupFromItemCache(itemCacheRef, code) || itemLookupMap.get(key);
      if (!itm) itm = await fetchItemByCodeFromFirestore(code);
      if (!itm) {
        setItems((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], code };
          return copy;
        });
        toast.error(`Item not found in Product Master: ${code}`);
        setTimeout(() => safeFocus(qtyRefs, i), 60);
        return;
      }
      mergeItemIntoMaster(itm);
      applyItemToRow(i, itm);
    },
    [itemLookupMap, fetchItemByCodeFromFirestore, mergeItemIntoMaster, applyItemToRow, safeFocus]
  );

  useEffect(() => {
    codeRefs.current = codeRefs.current || [];
    qtyRefs.current = qtyRefs.current || [];
    discRefs.current = discRefs.current || [];
    while (codeRefs.current.length < items.length) codeRefs.current.push(null);
    while (qtyRefs.current.length < items.length) qtyRefs.current.push(null);
    while (discRefs.current.length < items.length) discRefs.current.push(null);
    codeRefs.current.length = items.length;
    qtyRefs.current.length = items.length;
    discRefs.current.length = items.length;
  }, [items.length]);

  const handleItemKeyDown = (e, i, field) => {
    const colRefs = { code: codeRefs, qty: qtyRefs, discount: discRefs };
    if (e.key === "Enter" && field === "code") {
      const val = e.target.value;
      void selectItem(i, val);
      e.preventDefault();
      return;
    }
    let nextRow = i; let nextField = field;
    if (["Enter", "Tab"].includes(e.key)) {
      if (field === "code") nextField = "qty";
      else if (field === "qty") nextField = "discount";
      else { nextField = "code"; nextRow = i + 1; }
      e.preventDefault();
    } else if (e.key === "ArrowDown") nextRow = Math.min(items.length - 1, i + 1);
    else if (e.key === "ArrowUp") nextRow = Math.max(0, i - 1);
    else return;

    if (nextRow >= items.length) {
      setItems(prev => {
        const next = [...prev, { ...EMPTY_ROW_TEMPLATE }];
        setTimeout(() => safeFocus(colRefs[nextField], next.length - 1), 60);
        return next;
      });
    } else {
      setTimeout(() => safeFocus(colRefs[nextField], nextRow), 40);
    }
  };

  // Dropdown + advanced barcode parsing + intelligent fill
  const [codeDropdown, setCodeDropdown] = useState({ open: false, row: null, search: "", width: 420 });
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const filterTimeoutRef = useRef(null);
  const productWarmStartedRef = useRef(false);

  const filterItemMaster = useCallback((q) => {
    const ql = (q ?? "").toString();
    const trimmed = ql.trim();
    setCodeDropdown((prev) => ({ ...prev, search: ql }));
    setFilteredItems(filterAndSortCachedProducts(itemCacheRef.current, ql, ITEM_DROPDOWN_MAX));
    setHighlightIndex(0);
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    if (trimmed) setItemSearchLoading(true);
    else setItemSearchLoading(false);
    filterTimeoutRef.current = setTimeout(() => {
      void searchItemsForDropdown(trimmed);
    }, ITEM_SEARCH_DEBOUNCE_MS);
  }, [searchItemsForDropdown]);

  const openCodeDropdown = (rowIdx, inputEl) => {
    if (!inputEl) return;
    const q = (inputEl.value || "").toString();
    const width = Math.max(420, inputEl.offsetWidth || 420);
    setCodeDropdown({ open: true, row: rowIdx, search: q, width });
    filterItemMaster(q);
    if (!productWarmStartedRef.current) {
      productWarmStartedRef.current = true;
      void warmProductMasterCache(itemCacheRef.current, 50).then(() => {
        filterItemMaster(codeRefs.current?.[rowIdx]?.value ?? q);
      });
    }
  };
  const closeCodeDropdown = () => {
    setCodeDropdown((prev) => ({ ...prev, open: false, row: null }));
    setFilteredItems([]);
    setHighlightIndex(0);
    setItemSearchLoading(false);
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
  };
  const chooseItemFromDropdown = (rowIdx, item) => {
    if (!item) return;
    skipCodeBlurRef.current = true;
    void selectItem(rowIdx, item.partno || item.code || item.barcode || "").finally(() => {
      setTimeout(() => {
        skipCodeBlurRef.current = false;
      }, 50);
    });
    closeCodeDropdown();
  };

  const handleCodeInputKeyDown = (e, rowIdx) => {
    if (!codeDropdown.open || codeDropdown.row !== rowIdx) return false;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((hi) => Math.min((filteredItems.length - 1) || 0, hi + 1));
      return true;
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((hi) => Math.max(0, hi - 1));
      return true;
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = filteredItems[highlightIndex];
      if (sel) chooseItemFromDropdown(rowIdx, sel);
      else {
        const typed = codeRefs.current?.[rowIdx]?.value;
        if (typed) selectItem(rowIdx, typed);
        closeCodeDropdown();
      }
      return true;
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeCodeDropdown();
      return true;
    }
    return false;
  };

  const handleCodeInputComplete = async (rowIdx, value) => {
    try {
      if (skipCodeBlurRef.current) return;
      const v = (value || "").toString().trim();
      if (!v) return;
      const row = items[rowIdx];
      if (
        row &&
        String(row.code || "").trim().toLowerCase() === v.toLowerCase() &&
        String(row.desc || "").trim()
      ) {
        return;
      }
      if (v.includes("/")) {
        const parts = v.split("/").map(p => p.trim());
        if (parts.length >= 6) {
          const itemCode = (parts[3] || "").trim();
          const qtyRaw = (parts[4] || "").replace(/^0+/, "") || parts[4] || "0";
          const qty = Number(qtyRaw) || 0;
          const amtRaw = (parts[5] || "").replace(/^0+/, "") || parts[5] || "0";
          const amount = Number(amtRaw) || 0;
          if (itemCode) {
            await selectItem(rowIdx, itemCode);
            if (qty) handleChange(rowIdx, "qty", qty);
            if (amount) handleChange(rowIdx, "rate", String(amount));
            return;
          }
        }
      }
      await selectItem(rowIdx, v);
    } catch (err) {
      console.error("handleCodeInputComplete failed", err);
    }
  };

  useEffect(() => {
    const onDocClick = (ev) => {
      const dd = document.getElementById("item-grid-dropdown");
      if (!dd) return;
      const clickedInsideDropdown = dd.contains(ev.target);
      const clickedInsideCodeInput =
        !!ev.target?.closest?.('input[data-col="code"]') ||
        !!ev.target?.closest?.('[data-col="code"]');
      if (!clickedInsideDropdown && !clickedInsideCodeInput) closeCodeDropdown();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [filteredItems]);

  useEffect(() => () => {
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!codeDropdown.open) return;
    const dd = document.getElementById("item-grid-dropdown");
    if (!dd) return;
    const rows = dd.querySelectorAll("tbody tr");
    const row = rows[highlightIndex];
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, codeDropdown.open, filteredItems]);

  /* ---------------- global shortcuts & ctrl-delete ---------------- */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "F5") {
        e.preventDefault();
        if (isImportedFindVoucher || !currentInvoiceId) handleSave();
        else modifyInvoice();
        return;
      }
      if (e.key === "F2") { e.preventDefault(); setShowInvoiceList(true); return; }
      if (e.key === "F3") { e.preventDefault(); newInvoice(); return; }
      if (e.key === "F4") {
        e.preventDefault();
        if (!isImportedFindVoucher && currentInvoiceId) modifyInvoice();
        else handleSave();
        return;
      }
      if (e.key === "F1") {
        e.preventDefault(); const active = document.activeElement; if (!active) return;
        if (partyRef.current && (active === partyRef.current || (partyRef.current.contains && partyRef.current.contains(active)))) {
          setShowQuickParty(true);
          return;
        }
        const col = active.dataset?.col;
        const rowIdx = active.dataset?.row !== undefined ? Number(active.dataset.row) : null;
        if (col === "code") {
          setItemModalRow(rowIdx);
          setShowItemModal(true);
          return;
        }
        const tr = active.closest ? active.closest("tr") : null;
        if (tr) {
          const codeInput = tr.querySelector('input[data-col="code"]');
          if (codeInput) {
            const idx = codeInput.dataset?.row !== undefined ? Number(codeInput.dataset.row) : null;
            setItemModalRow(idx);
            setShowItemModal(true);
            return;
          }
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSave, modifyInvoice, newInvoice, loadInvoiceList, currentInvoiceId, isImportedFindVoucher]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "Delete")) {
        const active = document.activeElement;
        let rowIdx = null;
        if (active && active.dataset && active.dataset.row !== undefined) rowIdx = Number(active.dataset.row);
        else {
          const tr = active && active.closest ? active.closest('tr') : null;
          if (tr) {
            const tbody = tr.closest('tbody');
            if (tbody) {
              const rows = Array.from(tbody.querySelectorAll('tr'));
              rowIdx = rows.indexOf(tr);
            }
          }
        }
        if (rowIdx == null || isNaN(rowIdx) || rowIdx < 0) return;
        e.preventDefault();
        setItems(prev => {
          if (prev.length <= 1) {
            const copy = [...prev];
            copy[rowIdx] = { ...EMPTY_ROW_TEMPLATE };
            setTimeout(() => safeFocus(codeRefs, rowIdx), 40);
            return copy;
          }
          const next = prev.filter((_, idx) => idx !== rowIdx);
          setTimeout(() => {
            const focusIdx = Math.min(rowIdx, next.length - 1);
            if (focusIdx >= 0) safeFocus(codeRefs, focusIdx);
          }, 40);
          return next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items.length, safeFocus]);

  /* ---------------- onItemCreated & onQuickPartyCreated ---------------- */
  const onItemCreated = (newItem) => {
    const normalized =
      newItem?.partno != null || newItem?.alias != null
        ? newItem
        : normalizeFirestoreItemDoc({ id: newItem?.id, data: () => newItem || {} });
    mergeItemIntoMaster(normalized);
    if (itemModalRow !== null && itemModalRow >= 0) {
      applyItemToRow(itemModalRow, normalized);
    }
    setItemModalRow(null);
  };

  const onQuickPartyCreated = (newParty) => {
    setPartyList(prev => [newParty, ...(prev || [])]);
    setSelectedParty(newParty);
  };

  /* ---------------- dropdown and UI render continue below ---------------- */

  return (
    <div className="h-screen flex flex-col bg-[#f1f2f4] overflow-hidden">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <div className="flex-shrink-0">
        <VoucherHeader
          voucherNo={voucherNo}
          setVoucherNo={setVoucherNo}
          invoiceDate={invoiceDate}
          setInvoiceDate={setInvoiceDate}
          voucherTypeLabel={voucherTypeLabel}
        />
      </div>

      <QueuedInvoicesModal
        open={showQueueModal}
        onClose={() => setShowQueueModal(false)}
        onLoad={(payload, queueId) => {
          setItems(payload.items && payload.items.length ? payload.items : [{ ...EMPTY_ROW_TEMPLATE }]);
          setSelectedParty(payload.selectedParty || (payload.partyName ? { partyName: payload.partyName } : null));
          setDeliveryAddress(payload.deliveryAddress || {});
          setInvoiceDate(payload.invoiceDateStr || payload.invoiceDate || new Date().toISOString().split("T")[0]);
          setTransactionClass(payload.transactionClass || "");
          setCashCredit(payload.cashCredit || "CASH");
          setIsInterState(Boolean(payload.isInterState));
          setPayment(payload.payment || { mode: "Cash", amountPaid: 0, details: {} });
          setVoucherNo(payload.clientToken || "");
          setPackingAndForwarding(Number(payload.packingAndForwarding) || 0);
          setTimeout(() => safeFocus(codeRefs, 0), 40);
          try { fetchPartyBalance(payload.selectedParty || (payload.partyName ? { partyName: payload.partyName } : null)); } catch (e) { /* ignore */ }
        }}
      />

      {/* Party row */}
      <div className="flex-shrink-0 bg-[#e9eaee] px-3 py-2 border-x border-gray-400 text-xs grid grid-cols-12 gap-3 items-center">
        <label className="col-span-3 flex items-center gap-2">
          <span className="w-20 text-right">Account/Party:</span>
          <div className="flex-1 flex items-center gap-2">
            <div className="flex items-center gap-2 w-full">
              <PartyAutoComplete
                partyList={partyList}
                selectedParty={selectedParty}
                setSelectedParty={setSelectedParty}
                allowedGroup={partyGroup}
                inputRef={partyRef}
                onEnter={() => safeFocus(transRef)}
                placeholder={isInward ? "Type supplier name and Enter" : "Type party name and Enter"}
              />
              {!isInward && (
                <div title="Party balance (invoices - payments)" className="min-w-[50px] text-right">
                  {balanceLoading ? (
                    <span className="text-gray-500">Loading…</span>
                  ) : (
                    <span
                      className={
                        partyBalance > 0
                          ? "text-red-600"
                          : partyBalance < 0
                            ? "text-green-600"
                            : "text-gray-700"
                      }
                    >
                      {formatCurrency(partyBalance)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </label>

        <label className="col-span-3 flex flex-col">
          <div className="flex items-center gap-2">
            <span className="w-60 text-right">Transaction Class:</span>
            <input
              ref={transRef}
              list="transList"
              value={transactionClass}
              onChange={(e) => setTransactionClass(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  safeFocus(isInward ? codeRefs : cashRef, 0);
                }
              }}
              className="border border-gray-400 bg-white px-2 py-[3px] w-full"
              placeholder="Select or type..."
            />
            <datalist id="transList">
              {transactionClasses.map((t, idx) => <option key={idx} value={t} />)}
            </datalist>
          </div>
        </label>

        {!isInward && (
        <label className="col-span-3 flex flex-wrap items-center gap-3">
          <span className="w-24 text-right shrink-0">Sale type:</span>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                ref={cashRef}
                type="radio"
                name="billingSaleType"
                checked={cashCredit === "CASH"}
                onChange={() => setCashCredit("CASH")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    safeFocus(codeRefs, 0);
                  }
                }}
              />
              <span>Cash sale</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="billingSaleType"
                checked={cashCredit === "CREDIT"}
                onChange={() => {
                  setCashCredit("CREDIT");
                  const d = new Date(invoiceDate || Date.now());
                  d.setDate(d.getDate() + 30);
                  setCreditDueDate(d.toISOString().slice(0, 10));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    safeFocus(codeRefs, 0);
                  }
                }}
              />
              <span>Credit sale</span>
            </label>
            {cashCredit === "CREDIT" && (
              <>
                <label className="flex items-center gap-1.5 text-xs">
                  <span className="shrink-0 text-gray-600">Outlet</span>
                  <select
                    value={creditOutletBranch}
                    onChange={(e) => setCreditOutletBranch(e.target.value)}
                    className="max-w-[10rem] border border-gray-400 bg-white px-2 py-1"
                    title="Tags the Udhar (credit) bill to this shop — same list as Credit / Due branch"
                  >
                    {billingCreditBranches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  <span className="text-gray-600">Due date</span>
                  <input
                    type="date"
                    value={creditDueDate}
                    onChange={(e) => setCreditDueDate(e.target.value)}
                    className="border border-gray-400 bg-white px-2 py-1"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-xs max-w-[16rem]">
                  <input
                    type="checkbox"
                    checked={openCreditDueAfterSave}
                    onChange={(e) => setOpenCreditDueAfterSave(e.target.checked)}
                  />
                  <span className="text-gray-700">After save, go to Credit / Due</span>
                </label>
              </>
            )}
          </div>
        </label>
        )}

        <label className="flex min-w-[50px] items-center gap-2">
          <input type="checkbox" checked={isInterState} onChange={(e) => setIsInterState(e.target.checked)} />
          <span>Inter-state (IGST)</span>
        </label>

        <label className="col-span-1 flex items-center gap-2">
          <span className="w-20 text-right">Employee:</span>
          <div className="flex-2">
            <PartyAutoComplete
              partyList={partyList}
              selectedParty={selectedEmployee}
              setSelectedParty={(p) => { setSelectedEmployee(p); if (p) fetchEmployeeCommissions(p); }}
              allowedGroup="employee"
              inputRef={null}
              onEnter={() => safeFocus(transRef)}
              placeholder="Select employee..."
            />
          </div>
        </label>
      </div>

      {/* Tabs + column width settings (outward grid) */}
      <div className="flex-shrink-0 bg-[#cfd1d6] px-2 py-1 border-x flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex gap-2">
          <button type="button" onClick={() => setActiveTab("items")} className={`px-3 py-1 rounded ${activeTab === "items" ? "bg-white font-bold" : ""}`}>Item Listing</button>
          <button type="button" onClick={() => setActiveTab("delivery")} className={`px-3 py-1 rounded ${activeTab === "delivery" ? "bg-white font-bold" : ""}`}>Delivery Address</button>
        </div>
        {activeTab === "items" ? (
          <button
            type="button"
            onClick={() => {
              setDraftColWidths([...columnWidths]);
              setColumnSettingsOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-400/80 bg-white px-2.5 py-1 font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            title="Sl., Item Code, Item Description, … — width in pixels"
          >
            <Settings2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Column widths
          </button>
        ) : null}
      </div>

      {/* main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === "delivery" ? (
          <div className="border-x border-b p-3 text-xs space-y-2 bg-white overflow-auto flex-1">
            <div className="grid grid-cols-2 gap-3">
              <label><div>Party Name</div><input value={deliveryAddress.partyName || ""} onChange={(e) => setDeliveryAddress(d => ({ ...d, partyName: e.target.value }))} className="border col-span-2 px-2 py-2 w-2/4" /></label>
              <label><div>GSTIN</div><input value={deliveryAddress.gstin || ""} onChange={(e) => setDeliveryAddress(d => ({ ...d, gstin: e.target.value }))} className="border col-span-2 px-2 py-1 w-2/4" /></label>
              <label className="col-span-3"><div>Address</div><textarea value={deliveryAddress.address || ""} onChange={(e) => setDeliveryAddress(d => ({ ...d, address: e.target.value }))} className="border px-2 py-1 w-2/4" /></label>
              <label><div>Phone</div><input value={deliveryAddress.phone || ""} onChange={(e) => setDeliveryAddress(d => ({ ...d, phone: e.target.value }))} className="border px-2 py-1 w-2/4" /></label>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-12 bg-white border-x border-b overflow-hidden">
            <div className="col-span-10 border-r overflow-auto">
              <table className="w-full text-xs border-collapse table-fixed">
                <colgroup>
                  {columnWidths.map((w, i) => (
                    <col key={i} style={{ width: `${w}px` }} />
                  ))}
                </colgroup>
                <thead className="bg-[#d8d9de] sticky top-0">
                  <tr>{cols.map((h, hi) => <th key={`${h}-${hi}`} className="border px-1 py-1 text-[11px] text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.map((r, i) => {
                    const { gross, discAmt, sgstAmt = 0, cgstAmt = 0, igstAmt = 0, netAmount } = calcRow(r);
                    const valid = rowValidation[i]?.ok ?? true;
                    return (
                      <tr key={i} className={`${valid ? "" : "bg-red-50"} h-4`} style={{ transition: "background-color .12s", lineHeight: "1.2" }}>
                        <td className="border px-1 py-0.5 text-xs align-middle">{i + 1}</td>
                        <td className="border px-1 py-0.5" style={{ position: "relative" }}>
                          <input
                            ref={el => codeRefs.current[i] = el}
                            data-row={i}
                            data-col="code"
                            value={r.code ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleChange(i, "code", val);
                              if (!codeDropdown.open || codeDropdown.row !== i) {
                                openCodeDropdown(i, e.target);
                              } else {
                                filterItemMaster(val);
                              }
                            }}
                            onBlur={(e) => { try { handleCodeInputComplete(i, e.target.value); } catch (err) { console.error(err); } }}
                            onFocus={(e) => openCodeDropdown(i, e.target)}
                            onKeyDown={(e) => {
                              const handled = handleCodeInputKeyDown(e, i);
                              if (handled) return;
                              handleItemKeyDown(e, i, "code");
                            }}
                            className="w-full box-border h-2 min-h-5 border px-1 text-xs truncate whitespace-nowrap"
                            style={{ lineHeight: "1.2" }}
                          />
                          {codeDropdown.open && codeDropdown.row === i && (
                            <ItemGridDropdown
                              items={filteredItems}
                              loading={itemSearchLoading}
                              searchQuery={codeDropdown.search}
                              highlightIndex={highlightIndex}
                              setHighlightIndex={setHighlightIndex}
                              onSelect={(item) => chooseItemFromDropdown(i, item)}
                              style={{ top: "100%", left: 0, minWidth: Math.max(480, codeDropdown.width) }}
                            />
                          )}
                          {/* No datalist: rendering 10k+ options freezes the browser. Use the dropdown only. */}
                        </td>
                        <td className="border px-1 py-0.5 text-xs align-middle">
                          <div className="truncate whitespace-nowrap overflow-hidden" title={r.desc || ""}>{r.desc || ""}</div>
                        </td>
                        <td className="border px-1 py-0.5">
                          <input
                            ref={el => qtyRefs.current[i] = el}
                            data-row={i}
                            value={r.qty ?? ""}
                            type="text"
                            onChange={(e) => handleChange(i, "qty", e.target.value)}
                            onKeyDown={(e) => handleItemKeyDown(e, i, "qty")}
                            className="w-full box-border h-2 min-h-5 border px-1 text-xs text-right"
                            style={{ lineHeight: "1.2" }}
                          />
                        </td>
                        <td className="border px-1 py-0.5 text-xs align-middle">{resolveUnit(r.unit)}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{r.rate ?? ""}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{gross.toFixed(2)}</td>
                        <td className="border px-1 py-0.5">
                          <input
                            ref={el => discRefs.current[i] = el}
                            data-row={i}
                            value={r.discount ?? ""}
                            type="text"
                            onChange={(e) => handleChange(i, "discount", e.target.value)}
                            onKeyDown={(e) => handleItemKeyDown(e, i, "discount")}
                            className="w-full box-border h-2 min-h-5 border px-1 text-xs text-right"
                            style={{ lineHeight: "1.2" }}
                          />
                        </td>
                        <td className="border px-1 py-0.5 text-xs text-right">{discAmt.toFixed(2)}</td>
                        <td className="border px-1 py-0.5 text-xs">GST {(r.gst ?? r.GST ?? DEFAULT_GST)}%</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{(isInterState ? (((r.gst ?? r.GST ?? DEFAULT_GST)).toFixed(2) + "%") : (((r.gst ?? r.GST ?? DEFAULT_GST) / 2).toFixed(2) + "%"))}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{(isInterState ? igstAmt.toFixed(2) : sgstAmt.toFixed(2))}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{(!isInterState ? (((r.gst ?? r.GST ?? DEFAULT_GST) / 2).toFixed(2) + "%") : "")}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{(!isInterState ? cgstAmt.toFixed(2) : "")}</td>
                        <td className="border px-1 py-0.5 text-xs text-right">{netAmount.toFixed(2)}</td>
                        {/* ALWAYS show numeric commission (0.00 when zero) */}
                        <td className="border px-1 py-0.5 text-xs text-right">{Number(r.commissionAmount || 0).toFixed(2)}</td>
                        <td className="border px-1 py-0.5 text-xs truncate">{r.mrp ?? ""}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-semibold sticky bottom-0">
                    <td colSpan={3} className="p-2 text-right">Totals:</td>
                    <td className="p-2 text-right">{totals.qty}</td>
                    <td colSpan={2}></td>
                    <td className="p-2 text-right">{totals.gross.toFixed(2)}</td>
                    <td></td>
                    <td className="p-2 text-right">{totals.discount.toFixed(2)}</td>
                    <td></td><td></td>
                    <td className="p-2 text-right">{isInterState ? totals.igst.toFixed(2) : totals.sgst.toFixed(2)}</td>
                    <td></td>
                    <td className="p-2 text-right">{!isInterState ? totals.cgst.toFixed(2) : ""}</td>
                    <td className="p-2 text-right">{totals.net.toFixed(2)}</td>
                    <td className="p-2 text-right">{Number(commissionTotals.total || 0).toFixed(2)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-gray-50 sticky bottom-0">
                    <td colSpan={10}></td>
                    <td className="p-2 text-right">Round Off</td>
                    <td className="p-2 text-right">{roundOff.toFixed(2)}</td>

                    <td className="p-2 text-right">Packing & Forwarding</td>
                    <td className="p-2 text-right">
                      <input
                        type="number"
                        value={packingAndForwarding}
                        onChange={(e) => setPackingAndForwarding(Number(e.target.value) || 0)}
                        className="border px-2 py-1 text-right w-20"
                      />
                    </td>

                    <td className="p-2 text-right">Grand Total</td>
                    <td className="p-2 text-right">{grandTotalDisplay}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Side Buttons */}
            <div className="col-span-2 bg-[#eeeff3] p-1 text-xs space-y-2 overflow-auto">
              <SideBtn
                onClick={() => {
                  if (isImportedFindVoucher || !currentInvoiceId) handleSave();
                  else modifyInvoice();
                }}
                className="bg-gray-300"
                disabled={saving}
              >
                {saving
                  ? "💾 Saving…"
                  : `💾 Save (${voucherNo || "sales import"})`}
              </SideBtn>
              <SideBtn
                onClick={modifyInvoice}
                className="bg-gray-300"
                title="Saves to sales_import_months lineChunks"
              >
                ✏️ Modify
              </SideBtn>
              <SideBtn onClick={newInvoice} className="bg-gray-300">🆕 New</SideBtn>
              <SideBtn onClick={() => { loadInvoiceList(); setShowInvoiceList(true); }} className="bg-gray-300">🔎 Find</SideBtn>
              <SideBtn onClick={deleteInvoice} className="bg-red-300">🗑️ Delete</SideBtn>
              <SideBtn onClick={async () => {
                try {
                  startProcessing();
                  toast.success("Retry started — check console for logs.");
                } catch (err) {
                  console.error(err);
                  toast.error("Retry failed — see console");
                }
              }}>
                ↻ Retry Queue
              </SideBtn>
              <SideBtn onClick={async () => {
                const items = await getAllQueueItems();
                console.log("Queued items:", items);
                toast.success(`Queued items logged to console (${items.length})`);
              }}>
                📝 Show Queue (console)
              </SideBtn>
              <SideBtn onClick={() => setShowQueueModal(true)}>📦 Queued Invoices</SideBtn>
              <SideBtn>
                <label className="cursor-pointer">
                  Import Excel
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => {
                    const file = e?.target?.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const data = new Uint8Array(event.target.result);
                      const workbook = XLSX.read(data, { type: "array" });
                      const sheet = workbook.Sheets[workbook.SheetNames[0]];
                      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                      const mapped = json.map((row) => {
                        const norm = {};
                        Object.keys(row).forEach(k => { norm[k.toLowerCase().trim()] = row[k]; });
                        return {
                          code: norm.code || norm.itemcode || norm["item code"] || "",
                          desc: norm.desc || norm.description || "",
                          qty: Number(norm.qty || norm.qtypcs || norm["quantity"] || 0),
                          unit: resolveUnit(norm.unit || norm.uom || norm["unit"]),
                          rate: String(norm.rate || norm.price || 0),
                          discount: Number(norm.discount || 0),
                          gst: Number(norm.gst || norm.tax || DEFAULT_GST),
                          mrp: norm.mrp || "",
                        };
                      });
                      setItems(mapped.length ? mapped : [{ ...EMPTY_ROW_TEMPLATE }]);
                    };
                    reader.readAsArrayBuffer(file);
                  }} />
                </label>
              </SideBtn>
              <SideBtn onClick={() => { setShowInvoiceList(true); }}>Load Voucher.. (F2)</SideBtn>
              <SideBtn onClick={() => setShowQuickParty(true)}>+ Quick Party (F1)</SideBtn>
              <div className="border-t border-gray-300 my-2" />

              {/* Payment UI */}
              <div className="p-2 bg-white border rounded">
                <div className="mb-2 font-semibold text-sm">Payment</div>
                <div className="flex gap-2 mb-2">
                  <button className={`px-2 py-1 border rounded ${payment.mode === "Cash" ? "bg-gray-200" : ""}`} onClick={() => setPayment({ ...payment, mode: "Cash" })} type="button">Cash</button>
                  <button className={`px-2 py-1 border rounded ${payment.mode === "UPI" ? "bg-gray-200" : ""}`} onClick={() => setPayment({ ...payment, mode: "UPI" })} type="button">UPI</button>
                  <button className={`px-2 py-1 border rounded ${payment.mode === "Card" ? "bg-gray-200" : ""}`} onClick={() => setPayment({ ...payment, mode: "Card" })} type="button">Card</button>
                </div>
                <div className="mb-2">
                  <label className="block text-xs">Amount Paid</label>
                  <input type="number" className="border px-2 py-1 w-full" value={payment.amountPaid} onChange={(e) => setPayment({ ...payment, amountPaid: Number(e.target.value) })} />
                </div>
                {payment.mode === "UPI" && (
                  <div className="mb-2">
                    <label className="block text-xs">UPI ID</label>
                    <input className="border px-2 py-1 w-full" value={payment.details.upi || ""} onChange={(e) => setPayment({ ...payment, details: { ...payment.details, upi: e.target.value } })} />
                  </div>
                )}
                {payment.mode === "Card" && (
                  <div className="mb-2">
                    <label className="block text-xs">Card last 4 digits</label>
                    <input className="border px-2 py-1 w-full" value={payment.details.cardLast4 || ""} onChange={(e) => setPayment({ ...payment, details: { ...payment.details, cardLast4: e.target.value } })} />
                  </div>
                )}
              </div>

              {/* Commission summary */}
              <div className="p-2 bg-white border rounded mt-2">
                <div className="font-semibold text-sm">Commission</div>
                <div className="text-sm mt-1">Employee: {selectedEmployee ? (selectedEmployee.partyName || selectedEmployee.name) : "—"}</div>
                <div className="text-lg font-semibold mt-1">{formatCurrency(commissionTotals.total || 0)}</div>
              </div>

              <div className="border-t border-gray-300 my-2" />
              <BillingFindVoucherModal
                open={showInvoiceList}
                onClose={() => setShowInvoiceList(false)}
                loading={invoiceListLoading}
                onRefresh={() => loadInvoiceList(true)}
                vouchers={invoiceList}
                voucherTypeLabel={voucherTypeLabel}
                onOpenVoucher={(inv) => loadInvoice(inv)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ColumnWidthSettingsModal
        open={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        columnLabels={cols}
        draftWidths={draftColWidths}
        setDraftWidths={setDraftColWidths}
        onSave={() => {
          const next = draftColWidths.map((w, i) => {
            const x = Number(w);
            if (!Number.isFinite(x)) return DEFAULT_BILLING_COL_WIDTHS[i];
            return Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, Math.round(x)));
          });
          setColumnWidths(next);
          saveBillingColumnWidths(next, colWidthsKey);
          setColumnSettingsOpen(false);
          toast.success("Column widths saved");
        }}
        onResetDefaults={() => setDraftColWidths([...DEFAULT_BILLING_COL_WIDTHS])}
        voucherTypeLabel={voucherTypeLabel}
      />
      <QuickAddPartyModal open={showQuickParty} onClose={() => setShowQuickParty(false)} onCreated={onQuickPartyCreated} />
      <ItemCreateModal open={showItemModal} onClose={() => { setShowItemModal(false); setItemModalRow(null); }} onCreated={(ni) => { onItemCreated(ni); setShowItemModal(false); }} />
    </div>
  );
}
